// Serialization for the ask_user_question answer string, kept as pure functions
// so the round-trip is unit-testable (see choiceAnswer.test.ts).
//
// Multiple selections are joined with the ASCII Record Separator (U+001E),
// which can't occur in an option label or a newline — so `splitAnswer` recovers
// the exact selections regardless of label content (e.g. commas, as in the
// confirm labels "Yes, proceed" / "No, go back").
const ANSWER_SEP = '\u001E'

// `result` arrives serialized. Unwrap a JSON-encoded string, or a tool-result
// envelope ({ success, result, status }) where the runtime nests the answer
// under `result`; otherwise use verbatim. Exported so callers can compare the
// recovered answer exactly (e.g. against the dismissal sentinel) instead of
// substring-matching the raw envelope.
export const parseAnswer = (result: string | undefined): string => {
    if (!result) return ''
    try {
        const parsed: unknown = JSON.parse(result)
        if (typeof parsed === 'string') return parsed
        if (
            parsed !== null &&
            typeof parsed === 'object' &&
            'result' in parsed &&
            typeof (parsed as { result: unknown }).result === 'string'
        ) {
            return (parsed as { result: string }).result
        }
        return result
    } catch {
        return result
    }
}

// Join selected option labels into the single answer string sent to the agent.
export const joinAnswer = (parts: string[]): string => parts.join(ANSWER_SEP)

// Recover the individual answer tokens from a (possibly JSON-wrapped) result.
export const splitAnswer = (result: string | undefined): string[] =>
    parseAnswer(result)
        .split(ANSWER_SEP)
        .map((s) => s.trim())
        .filter(Boolean)
